import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { SearchComponent } from '../../molecules/search/search.component';
import InputType from '../../../shared/enums/input-type.enums';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import { ButtonComponent } from '../../molecules/button/button.component';
import ViewportType from '../../../shared/enums/viewport-type.enums';
import {
  concatMap,
  from,
  fromEvent,
  map,
  Observable,
  Subscription,
} from 'rxjs';
import { SearchGroup } from '../../../shared/interfaces/search-group.interface';
import { CapitalizePipe } from '../../capitalize.pipe';

@Component({
  selector: 'hdi-inpage-search',
  standalone: true,
  imports: [CommonModule, SearchComponent, ButtonComponent, CapitalizePipe],
  templateUrl: './inpage-search.component.html',
  styleUrl: './inpage-search.component.scss',
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class InpageSearchComponent implements OnDestroy, AfterViewInit {
  @Input() viewport: ViewportType = ViewportType.desktop;
  @Input() placeholder: string = 'Search';
  @Input() disabled: boolean = false;
  @Input() CTA: boolean = false;
  @Input() labelCTA: string = '';
  @Input() labelCTAMobile: string = this.labelCTA;
  @Input() flexDirection: string = 'row';
  @Input() flexDirectionMobile: string = this.flexDirection;
  @Input() buttonType: ButtonType = ButtonType.SQUARED_PRIMARY;
  @Input() buttonTypeMobile: ButtonType = this.buttonType;
  @Input() buttonIcon: string = 'arrow-right';
  @Input() buttonIconMobile: string = this.buttonIcon;
  @Input() expandButton: boolean = false;
  @Input() padding: boolean = true;
  @Input() filterContainerId?: string; // The container / Section where the item should be founded
  @Input() searchGroup?: SearchGroup[];
  @Output() handleInputChange: EventEmitter<string> =
    new EventEmitter<string>();
  inputType = InputType.Default;
  buttonDisabled: boolean = true;
  searchedElements: SearchGroup[] = [];
  private getSearchedElementSubs$: Subscription = new Subscription();
  private resetPreviousSearchedSubs$: Subscription = new Subscription();
  private container: HTMLElement | null = null;
  private foundedElement: Element | null | undefined;
  public hasError: boolean = false;
  public isEmpty: boolean = true;

  // Add DOM references:
  @ViewChild(SearchComponent, { static: false })
  searchComponent!: SearchComponent;
  @ViewChild('resultContainer') resultContainer!: ElementRef<HTMLUListElement>;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    if (this.searchComponent?.inputElement) {
      this.initializeSearchInput();
    } else {
      console.error('InPage Search > inputElement not resolved');
    }
  }
  private initializeSearchInput() {
    if (!this.searchComponent || !this.searchComponent.inputElement) {
      console.error('Search input not found');
      return;
    }

    const searchInputElement = this.searchComponent.inputElement;
    searchInputElement.addEventListener('keyup', () => {});

    fromEvent<KeyboardEvent>(searchInputElement, 'keyup').subscribe(() => {
      //const searchValue = searchInputElement.value.toLowerCase();
      // const searchLength = searchValue.length;
      // if (searchLength >= 3) {
      // } else {
      // }
    });
  }

  // Handle search result click
  inputChange(event: string) {
    this.buttonDisabled = event.length === 0;
    this.handleInputChange.emit(event);

    if (event.length >= 3 && this.filterContainerId) {
      this.hasError = false;
      this.getSearchedElementSubs$ = this.getSearchedElements(
        event,
        this.filterContainerId
      ).subscribe((elements) => {
        this.searchedElements = elements;
      });
    } else if (event.length < 3 && this.filterContainerId) {
      this.hasError = true;
      this.searchedElements = [];
    }

    if (event.length === 0) {
      this.hasError = false;
      this.isEmpty = true;
    } else {
      this.isEmpty = false;
    }
  }

  private getSearchedElements(
    event: string,
    filterContainerId: string
  ): Observable<SearchGroup[]> {
    this.container = this.document.getElementById(filterContainerId);
    if (!this.container) {
      return from([]);
    }
    return from([
      Array.from(this.container.querySelectorAll(`[title]`)).filter(
        (item: Element) =>
          item
            .getAttribute('title')
            ?.toLowerCase()
            .includes(event.toLowerCase())
      ),
    ]).pipe(
      map((elements) => {
        const suggestions = elements.map((item: Element) => item.textContent);
        if (!this.searchGroup) {
          console.error(
            'searchGroup in inpage-search.component.ts is undefined'
          );
          return [];
        }
        return this.searchGroup!.filter((group) => {
          return group.searched.some((searchedItem) =>
            suggestions.some(
              (suggestion) => suggestion?.trim() === searchedItem.trim()
            )
          );
        })
          .map((res) => {
            return {
              ...res,
              searched: res.searched.filter((searched) =>
                suggestions.some(
                  (suggestion) => suggestion?.trim() === searched.trim()
                )
              ),
            };
          })
          .map((searchGroup) => {
            return {
              ...searchGroup,
              templates: [
                ...searchGroup.searched.map((searched) => {
                  return {
                    label: searched,
                    template: searched
                      .toLowerCase()
                      .includes(event.toLowerCase())
                      ? searched.replace(
                          new RegExp(`(${event})`, 'i'),
                          (match) => `<strong>${match}</strong>`
                        )
                      : searched,
                  };
                }),
              ],
            };
          });
      })
    );
  }

  findElement(searchedElement: string) {
    this.resetPreviousSearchedSubs$ = this.resetPreviousSearched()
      .pipe(
        concatMap((previous) => {
          this.renderer.removeClass(previous, 'founded');
          return from([previous]);
        })
      )
      .subscribe(() => {
        this.foundedElement = this.container?.querySelector(
          `[title*="${searchedElement}"]`
        );
        this.renderer.addClass(this.foundedElement, 'founded');
        this.scrollIntoFounded();
        return;
      });
    this.foundedElement = this.container?.querySelector(
      `[title*="${searchedElement}"]`
    );
    this.renderer.addClass(this.foundedElement, 'founded');
    this.openAccordion();
    this.scrollIntoFounded();
  }

  private resetPreviousSearched(): Observable<Element> {
    const previousSearched = this.container?.getElementsByClassName('founded');
    if (!previousSearched || previousSearched.length === 0) {
      return from([]);
    } else {
      return from(previousSearched);
    }
  }

  private openAccordion() {
    const accordion = (this.foundedElement as HTMLElement).closest(
      '.accordion'
    );
    if (accordion) {
      const toggleButton = accordion?.querySelector('hdi-button')
        ?.firstChild as HTMLElement;
      const isAccordionOpen = toggleButton.classList.contains('open');
      if (!isAccordionOpen) {
        toggleButton.click();
      }
    }
  }

  private scrollIntoFounded() {
    this.foundedElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }

  closeSearchResults() {
    this.searchedElements = [];
    this.isEmpty = true;
  }

  ngOnDestroy(): void {
    this.getSearchedElementSubs$.unsubscribe();
    this.resetPreviousSearchedSubs$.unsubscribe();
  }
}
